import { Controller } from "@hotwired/stimulus";

// Plays a Lottie animation (JSON from app/assets/lottie/).
//
// Usage:
//   <div data-controller="lottie"
//        data-lottie-src-value="<%= asset_path("lottie/capture-flow.json") %>"
//        role="img" aria-label="..." class="aspect-[4/3]"></div>
//
// - The lottie-web player (~150 KB, light build) is dynamically imported, so it is
//   code-split and only downloaded on pages that actually use this controller.
// - Honors prefers-reduced-motion: renders a static first frame, no playback.
// - Starts when scrolled into view, pauses when scrolled out.
export default class extends Controller {
  static values = { src: String };

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.#load((lottie) => {
        this.anim = lottie.loadAnimation({
          container: this.element,
          renderer: "svg",
          loop: false,
          autoplay: false,
          path: this.srcValue,
        });
        this.anim.addEventListener("DOMLoaded", () => this.anim.goToAndStop(0, true));
      });
      return;
    }

    if ("IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) =>
            entry.isIntersecting ? this.#start() : this.#pause()
          );
        },
        { rootMargin: "100px" }
      );
      this.observer.observe(this.element);
    } else {
      this.#start();
    }
  }

  disconnect() {
    this.observer?.disconnect();
    this.anim?.destroy();
    this.anim = null;
  }

  #start() {
    if (this.anim) {
      this.anim.play();
      return;
    }
    this.#load((lottie) => {
      if (this.anim) return; // already started while the chunk was loading
      this.anim = lottie.loadAnimation({
        container: this.element,
        renderer: "svg",
        loop: true,
        autoplay: true,
        path: this.srcValue,
      });
    });
  }

  #pause() {
    this.anim?.pause();
  }

  #load(fn) {
    import("lottie-web/build/player/lottie_light").then((m) => fn(m.default));
  }
}
